import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { oidcEnabled, startSsoLogin } from '../auth/oidc.js';

const FEATURES = [
  {
    title: 'Unified contact center',
    body: 'Voice and chat in one agent workspace — recordings, transcripts, and per-speaker sentiment.',
  },
  {
    title: 'CRM that remembers',
    body: 'Every conversation, note, and disposition lives on the contact timeline your whole team shares.',
  },
  {
    title: 'Reseller-ready',
    body: 'Scoped portals, brands, and phone numbers so partners run their own desks under one roof.',
  },
];

/**
 * Public landing page shown to visitors who are not signed in. The navbar's
 * "Log in" action hands off to the central TIPSSSO portal and returns the user
 * to the workspace they were headed for.
 */
export function LandingPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const returnTo = params.get('redirect') || '/';

  // Already signed in → go straight to the app.
  if (user) {
    return <Navigate to={returnTo} replace />;
  }

  const handleLogin = () => {
    void startSsoLogin(returnTo);
  };

  return (
    <div className="landing">
      <header className="landing-navbar">
        <div className="landing-navbar__inner">
          <a className="landing-brand" href="/" aria-label="Tips Contact home">
            <img src="/tips-contact-logo.png" alt="Tips Contact" className="landing-brand__logo" />
          </a>
          <nav className="landing-navbar__actions">
            <a className="landing-nav-link" href="#features">
              Features
            </a>
            <button type="button" className="btn btn--primary" onClick={handleLogin}>
              Log in
            </button>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero__inner">
            <p className="landing-eyebrow">TIPS Contact</p>
            <h1 className="landing-title">The contact center built for the TIPS Data network.</h1>
            <p className="landing-subtitle">
              Run inbound and outbound conversations, keep every interaction on the contact record,
              and give resellers their own desk. Sign in with your TIPS account to get started.
            </p>
            <div className="landing-cta">
              <button type="button" className="btn btn--primary btn--lg" onClick={handleLogin}>
                Log in to continue
              </button>
              <a className="btn btn--lg" href="#features">
                See what's inside
              </a>
            </div>
            <p className="landing-hint">
              Single sign-on across the TIPS Data product family. No separate password to remember.
              {oidcEnabled && (
                <>
                  {' '}
                  <Link to="/login" className="landing-hint__link">
                    Sign in with a password instead
                  </Link>
                  .
                </>
              )}
            </p>
          </div>
        </section>

        <section id="features" className="landing-features">
          <div className="landing-features__grid">
            {FEATURES.map((f) => (
              <article key={f.title} className="landing-feature">
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <span>© {new Date().getFullYear()} TIPS Data</span>
        <button type="button" className="landing-footer__link" onClick={handleLogin}>
          Sign in
        </button>
      </footer>
    </div>
  );
}
